import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import { API_FRAUD_RULES } from "../../utils/apis";
import {
  createViewEdit,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_FRAUD_RULES } from "../../utils/urls";
import { FRAUD_RULE_CATEGORIES } from "./useFraudConfigurations";

const url = URL_FRAUD_RULES;

const FRAUD_RULE_NAMES: Record<string, string> = {
  // VELOCITY RULES
  VELOCITY_HIGH_FREQ_TX: "High-Frequency Transactions",
  VELOCITY_FAILED_TX: "Repeated Failed Transactions",
  VELOCITY_MULTIPLE_BENEFICIARIES: "Multiple Different Recipients",
  // AMOUNT RULES
  AMOUNT_HIGH_VALUE_SPIKE: "High-Value Transaction Spike",
  AMOUNT_THRESHOLD_EXCEEDED: "Large Transaction Amount",
  AMOUNT_ROUND_NUMBER: "Round Number Transaction",
  // GEO RULES
  GEO_NEW_DEVICE: "New Device Login",
  GEO_IMPOSSIBLE_TRAVEL: "Impossible Travel",
  GEO_HIGH_RISK_COUNTRY: "Transaction from High-Risk Location",
  // BEHAVIOR RULES
  BEHAVIOR_FAILED_LOGIN: "Multiple Failed Login Attempts",
  BEHAVIOR_OFF_HOURS: "Off-Hours Activity",
  BEHAVIOR_DORMANT_ACCOUNT: "Dormant Account Reactivation",
  // PATTERN RULES
  PATTERN_STRUCTURED_TX: "Structured Transactions",
  PATTERN_CIRCULAR_TX: "Circular Transaction Pattern",
};

const FRAUD_RULE_DESCRIPTIONS: Record<string, string> = {
  // VELOCITY RULES
  VELOCITY_HIGH_FREQ_TX: "Multiple transactions in rapid succession",
  VELOCITY_FAILED_TX: "Multiple failed transactions indicating testing",
  VELOCITY_MULTIPLE_BENEFICIARIES:
    "Sending money to many different accounts rapidly",
  // AMOUNT RULES
  AMOUNT_HIGH_VALUE_SPIKE: "Transaction amount exceeds 200% of user average",
  AMOUNT_THRESHOLD_EXCEEDED: "Single transaction exceeds threshold",
  AMOUNT_ROUND_NUMBER: "Transaction with suspiciously round amount",
  // GEO RULES
  GEO_NEW_DEVICE: "Login from unrecognized device",
  GEO_IMPOSSIBLE_TRAVEL: "Geographic locations indicate impossible travel",
  GEO_HIGH_RISK_COUNTRY: "Activity from known high-risk geographic area",
  // BEHAVIOR RULES
  BEHAVIOR_FAILED_LOGIN: "Excessive failed password attempts",
  BEHAVIOR_OFF_HOURS: "Transaction during unusual hours for user",
  BEHAVIOR_DORMANT_ACCOUNT: "Transaction from account inactive for long period",
  // PATTERN RULES
  PATTERN_STRUCTURED_TX: "Multiple transactions just below reporting threshold",
  PATTERN_CIRCULAR_TX: "Money moving in circular pattern between accounts",
};

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  VELOCITY: "Transaction Velocity",
  AMOUNT: "Amount-Based Risk",
  GEO: "Geo-Location & Device",
  BEHAVIOR: "User Behavior",
  PATTERN: "Pattern Detection",
};

export const useFraudRules = () => {
  const tableColumns = () => [
    { id: "code", header: "Code", filter: {} },
    { id: "name", header: "Name", filter: {} },
    { id: "category", header: "Category", filter: {} },
    { id: "severity", header: "Severity", filter: {} },
    { id: "score_weight", header: "Score Weight", filter: {} },
    { id: "action_type", header: "Action Type", filter: {} },
    { id: "is_active", header: "Is Active", filter: {} },
    {
      id: "applies_to_user_types",
      header: "Applies To User Types",
      filter: {},
    },
    { id: "trigger_count", header: "Trigger Count", filter: {} },
    { id: "trigger_rate", header: "Trigger Rate", filter: {} },
    { id: "last_triggered", header: "Last Triggered", filter: {} },
    { id: "created_at", header: "Created At", filter: {} },
    { id: "updated_at", header: "Updated At", filter: {} },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        created_at: formatDateTime(item?.created_at),
        updated_at: formatDateTime(item?.updated_at),
        trigger_count: String(item?.trigger_count ?? 0),
        trigger_rate: String(item?.trigger_rate ?? 0),
        last_triggered: item?.last_triggered
          ? formatDateTime(item?.last_triggered)
          : "Never",
        actions: (
          <FormActions actions={[...createViewEdit(url, item?.id, true)]} />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Fraud Rule Details",
          fields: {
            code: item?.code,
            name: item?.name,
            description: item?.description,
            category: item?.category,
            severity: item?.severity,
            score_weight: item?.score_weight,
            action_type: item?.action_type,
            is_active: item?.is_active ? "Active" : "Inactive",
            applies_to_user_types: Array.isArray(item?.applies_to_user_types)
              ? item?.applies_to_user_types.join(", ")
              : item?.applies_to_user_types,
          },
        },
        {
          title: "Parameters",
          fields: item?.parameters || { message: "No parameters configured" },
        },
        {
          title: "Statistics",
          fields: {
            trigger_count: String(item?.trigger_count ?? 0),
            trigger_rate: String(item?.trigger_rate ?? 0),
            last_triggered: item?.last_triggered
              ? formatDateTime(item?.last_triggered)
              : "Never",
          },
        },
        {
          title: "Audit Information",
          fields: {
            created_by: item?.created_by_name,
            created_at: formatDateTime(item?.created_at),
            updated_at: formatDateTime(item?.updated_at),
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const useFraudRulesForm = ({
  form,
  updateFormValues,
}: TFormHookParams = {}) => {
  const categoryValue = form?.category
    ? CATEGORY_DISPLAY_NAMES[form.category] || form.category
    : "";

  const sections: IFormSection[] = [
    {
      title: "Fraud Rule Details",
      fields: [
        {
          field_name: "code",
          label: "Code",
          type: "select",
          props: {
            required: true,
            placeholder: "Select code...",
            onChange: (value: string) => {
              if (updateFormValues) {
                const updates: any = {
                  code: value,
                  // Clear all parameter fields when code changes
                  transaction_count: undefined,
                  time_window_minutes: undefined,
                  failed_count: undefined,
                  unique_beneficiaries: undefined,
                  spike_percentage: undefined,
                  threshold_amount: undefined,
                  min_amount: undefined,
                  min_distance_km: undefined,
                  blocked_countries: undefined,
                  off_hours_start: undefined,
                  off_hours_end: undefined,
                  dormant_days: undefined,
                  threshold_percentage: undefined,
                  time_window_hours: undefined,
                  min_participants: undefined,
                };
                if (FRAUD_RULE_NAMES[value]) {
                  updates.name = FRAUD_RULE_NAMES[value];
                }
                if (FRAUD_RULE_DESCRIPTIONS[value]) {
                  updates.description = FRAUD_RULE_DESCRIPTIONS[value];
                }
                if (FRAUD_RULE_CATEGORIES[value]) {
                  updates.category = FRAUD_RULE_CATEGORIES[value];
                  updates.category_display =
                    CATEGORY_DISPLAY_NAMES[FRAUD_RULE_CATEGORIES[value]];
                }
                updateFormValues(updates);
              }
            },
          },
          options_data: [
            // VELOCITY RULES
            {
              id: "VELOCITY_HIGH_FREQ_TX",
              name: "High-Frequency Transactions",
            },
            {
              id: "VELOCITY_FAILED_TX",
              name: "Repeated Failed Transactions",
            },
            {
              id: "VELOCITY_MULTIPLE_BENEFICIARIES",
              name: "Multiple Different Recipients",
            },
            // AMOUNT RULES
            {
              id: "AMOUNT_HIGH_VALUE_SPIKE",
              name: "High-Value Transaction Spike",
            },
            {
              id: "AMOUNT_THRESHOLD_EXCEEDED",
              name: "Large Transaction Amount",
            },
            {
              id: "AMOUNT_ROUND_NUMBER",
              name: "Round Number Transaction",
            },
            // GEO RULES
            {
              id: "GEO_NEW_DEVICE",
              name: "New Device Login",
            },
            {
              id: "GEO_IMPOSSIBLE_TRAVEL",
              name: "Impossible Travel",
            },
            {
              id: "GEO_HIGH_RISK_COUNTRY",
              name: "Transaction from High-Risk Location",
            },
            // BEHAVIOR RULES
            {
              id: "BEHAVIOR_FAILED_LOGIN",
              name: "Multiple Failed Login Attempts",
            },
            {
              id: "BEHAVIOR_OFF_HOURS",
              name: "Off-Hours Activity",
            },
            {
              id: "BEHAVIOR_DORMANT_ACCOUNT",
              name: "Dormant Account Reactivation",
            },
            // PATTERN RULES
            {
              id: "PATTERN_STRUCTURED_TX",
              name: "Structured Transactions",
            },
            {
              id: "PATTERN_CIRCULAR_TX",
              name: "Circular Transaction Pattern",
            },
          ],
        },
        {
          field_name: "name",
          label: "Name",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "description",
          label: "Description",
          type: "textarea",
          props: { required: true },
        },
        {
          field_name: "category_display",
          label: "Category",
          type: "text",
          props: {
            disabled: true,
            placeholder: "Select a code first...",
          },
          value: categoryValue,
        },
        {
          field_name: "category",
          label: "Category (Hidden)",
          type: "text",
          visible: false,
          props: { required: true },
        },
        {
          field_name: "severity",
          label: "Severity",
          type: "select",
          props: { required: true, placeholder: "Select severity..." },
          options_data: [
            {
              id: "LOW",
              name: "Low",
            },
            {
              id: "MEDIUM",
              name: "Medium",
            },
            {
              id: "HIGH",
              name: "High",
            },
            {
              id: "CRITICAL",
              name: "Critical",
            },
          ],
        },
        // VELOCITY_HIGH_FREQ_TX and PATTERN_STRUCTURED_TX parameters
        {
          field_name: "transaction_count",
          label: "Transaction Count",
          type: "number",
          props: { required: true },
          visible: ["VELOCITY_HIGH_FREQ_TX", "PATTERN_STRUCTURED_TX"].includes(
            form?.code,
          ),
        },
        {
          field_name: "time_window_minutes",
          label: "Time Window (Minutes)",
          type: "number",
          props: { required: true },
          visible: [
            "VELOCITY_HIGH_FREQ_TX",
            "VELOCITY_FAILED_TX",
            "VELOCITY_MULTIPLE_BENEFICIARIES",
            "GEO_IMPOSSIBLE_TRAVEL",
            "BEHAVIOR_FAILED_LOGIN",
            "BEHAVIOR_OFF_HOURS",
          ].includes(form?.code),
        },
        // VELOCITY_FAILED_TX parameters
        {
          field_name: "failed_count",
          label: "Failed Count",
          type: "number",
          props: { required: true },
          visible: ["VELOCITY_FAILED_TX", "BEHAVIOR_FAILED_LOGIN"].includes(
            form?.code,
          ),
        },
        // VELOCITY_MULTIPLE_BENEFICIARIES parameters
        {
          field_name: "unique_beneficiaries",
          label: "Unique Beneficiaries",
          type: "number",
          props: { required: true },
          visible: form?.code === "VELOCITY_MULTIPLE_BENEFICIARIES",
        },
        // AMOUNT_HIGH_VALUE_SPIKE parameters
        {
          field_name: "spike_percentage",
          label: "Spike Percentage",
          type: "number",
          props: { required: true },
          visible: form?.code === "AMOUNT_HIGH_VALUE_SPIKE",
        },
        // AMOUNT_THRESHOLD_EXCEEDED parameters
        {
          field_name: "threshold_amount",
          label: "Threshold Amount",
          type: "number",
          props: { required: true },
          visible: form?.code === "AMOUNT_THRESHOLD_EXCEEDED",
        },
        // AMOUNT_ROUND_NUMBER parameters
        {
          field_name: "min_amount",
          label: "Minimum Amount",
          type: "number",
          props: { required: true },
          visible: form?.code === "AMOUNT_ROUND_NUMBER",
        },
        // GEO_IMPOSSIBLE_TRAVEL parameters
        {
          field_name: "min_distance_km",
          label: "Minimum Distance (KM)",
          type: "number",
          props: { required: true },
          visible: form?.code === "GEO_IMPOSSIBLE_TRAVEL",
        },
        // GEO_HIGH_RISK_COUNTRY parameters
        {
          field_name: "blocked_countries",
          label: "Blocked Countries (comma-separated)",
          type: "text",
          props: { required: true },
          visible: form?.code === "GEO_HIGH_RISK_COUNTRY",
        },
        // BEHAVIOR_OFF_HOURS parameters
        {
          field_name: "off_hours_start",
          label: "Off Hours Start (Hour)",
          type: "number",
          props: { required: true },
          visible: form?.code === "BEHAVIOR_OFF_HOURS",
        },
        {
          field_name: "off_hours_end",
          label: "Off Hours End (Hour)",
          type: "number",
          props: { required: true },
          visible: form?.code === "BEHAVIOR_OFF_HOURS",
        },
        // BEHAVIOR_DORMANT_ACCOUNT parameters
        {
          field_name: "dormant_days",
          label: "Dormant Days",
          type: "number",
          props: { required: true },
          visible: form?.code === "BEHAVIOR_DORMANT_ACCOUNT",
        },
        // PATTERN_STRUCTURED_TX parameters
        {
          field_name: "threshold_percentage",
          label: "Threshold Percentage",
          type: "number",
          props: { required: true },
          visible: form?.code === "PATTERN_STRUCTURED_TX",
        },
        {
          field_name: "time_window_hours",
          label: "Time Window (Hours)",
          type: "number",
          props: { required: true },
          visible: ["PATTERN_STRUCTURED_TX", "PATTERN_CIRCULAR_TX"].includes(
            form?.code,
          ),
        },
        // PATTERN_CIRCULAR_TX parameters
        {
          field_name: "min_participants",
          label: "Minimum Participants",
          type: "number",
          props: { required: true },
          visible: form?.code === "PATTERN_CIRCULAR_TX",
        },
        {
          field_name: "score_weight",
          label: "Score Weight",
          type: "number",
          props: { required: true },
        },
        {
          field_name: "action_type",
          label: "Action Type",
          type: "select",
          props: { required: true, placeholder: "Select action type..." },
          options_data: [
            {
              id: "ALERT",
              name: "Generate Alert Only",
            },
            {
              id: "BLOCK",
              name: "Block Transaction",
            },
            {
              id: "FREEZE",
              name: "Freeze Account",
            },
            {
              id: "REVIEW",
              name: "Require Manual Review",
            },
          ],
        },
        {
          field_name: "applies_to_user_types",
          label: "User Type",
          type: "select",
          props: {
            required: true,
            placeholder: "Select user type(s)...",
            multiple: true,
          },
          options_data: [
            {
              id: "PUBLIC",
              name: "Public",
            },
            {
              id: "AGENT",
              name: "Agent",
            },
            {
              id: "MERCHANT",
              name: "Merchant",
            },
          ],
        },
        {
          field_name: "is_active",
          label: "Is Active",
          type: "select",
          props: { required: true },
          options_data: [
            {
              id: true,
              name: "True",
            },
            {
              id: false,
              name: "False",
            },
          ],
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  return {
    sections,
    redirect,
  };
};

export const submitFraudRules = (payload: any, id: string | number) => {
  const cleanPayload = Object.keys(payload).reduce((acc, key) => {
    if (
      !key.endsWith("_name") &&
      key !== "category_display" &&
      isNaN(Number(key))
    ) {
      acc[key] = payload[key];
    }
    return acc;
  }, {} as any);

  const parameters: any = {};
  const parameterFields = [
    "transaction_count",
    "time_window_minutes",
    "failed_count",
    "unique_beneficiaries",
    "spike_percentage",
    "threshold_amount",
    "min_amount",
    "min_distance_km",
    "blocked_countries",
    "off_hours_start",
    "off_hours_end",
    "dormant_days",
    "threshold_percentage",
    "time_window_hours",
    "min_participants",
  ];

  // Extract parameter fields from cleanPayload
  parameterFields.forEach((field) => {
    if (
      cleanPayload[field] !== undefined &&
      cleanPayload[field] !== null &&
      cleanPayload[field] !== ""
    ) {
      // Handle blocked_countries as array
      if (
        field === "blocked_countries" &&
        typeof cleanPayload[field] === "string"
      ) {
        parameters[field] = cleanPayload[field]
          .split(",")
          .map((c: string) => c.trim());
      } else {
        parameters[field] = cleanPayload[field];
      }
    }
  });

  const params: any = {
    ...cleanPayload,
    category: cleanPayload.category?.toLowerCase(),
  };

  if (Object.keys(parameters).length > 0) {
    params.parameters = parameters;
  }

  parameterFields.forEach((field) => {
    delete params[field];
  });

  if (id) {
    return {
      id,
      api: `${API_FRAUD_RULES}`,
      params,
    };
  }

  //   CREATE
  return {
    api: `${API_FRAUD_RULES}`,
    params,
  };
};
